/**
 * Einen Tag des Arbeitsplans öffnen (Etappe 6).
 *
 * - Hat der Tag ein Datum, ist er ein Termin: es öffnet das Termin-Widget des
 *   Planers (`EditTerminDrawer`) — Zeit, Ort, Personen, Pauschalen, Fotos — mit
 *   „Wer führt aus", den Leistungen und „Was muss da sein" dazu.
 * - Ohne Datum öffnet dasselbe Blatt in schmaler Form: Titel, Beschreibung,
 *   Wer führt aus, Leistungen, Was muss da sein. Personen kommen mit dem Datum.
 */
import { useEffect, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { fehlerText } from "@vimaana/ui";

import ArbeitstagTeil from "./ArbeitstagTeil";
import BedarfListe from "./BedarfListe";
import { EditTerminDrawer } from "../planer/EditTerminDrawer";
import { usePeople } from "../planer/planer-utils";
import { useArbeitstagAendern, useArbeitstagKopie, useArbeitstagLoeschen, useReihenfolge } from "../../hooks/useArbeitsplan";
import { useKalender, useKunden, useTeamMembers, useTeamOwners } from "../../hooks/usePlanung";
import { tagName, type Arbeitsplan, type PlanTag } from "../../lib/arbeitsplan";
import "../../styles/planung.css";

type Props = {
  plan: Arbeitsplan;
  tagId: string;
  onClose: () => void;
  /** Den Kalender öffnen, nur mit diesem Tag. */
  onKalender: (tagIds: string[]) => void;
  /** Nach „×2": die Kopie öffnen. */
  onOeffnen: (tagId: string) => void;
};

export default function ArbeitstagOeffnen(props: Props) {
  const tag = props.plan.tage.find((t) => t.id === props.tagId);
  if (!tag) return null;
  return tag.im_kalender ? <TagMitDatum {...props} tag={tag} /> : <TagOhneDatum {...props} tag={tag} />;
}

function TagMitDatum({ plan, tag, onClose: schliessen }: Props & { tag: PlanTag }) {
  const qc = useQueryClient();
  /* Zeit, Personen und Pauschalen speichert das Termin-Widget am Termin — danach
     den Plan und die Lage nachladen, sonst zeigt die Karte die alte Besetzung. */
  const onClose = () => { qc.invalidateQueries({ queryKey: ["projekt", plan.projekt_id] }); schliessen(); };
  const { data: termine, isPending } = useKalender(tag.datum, tag.datum);
  const { data: members = [] } = useTeamMembers();
  const { data: owners = [] } = useTeamOwners();
  const { data: kunden = [] } = useKunden();
  const people = usePeople(members, owners);
  const t = termine?.find((x) => x.id === tag.id);
  if (!t) {
    return (
      <div className="pl-overlay" onClick={onClose}>
        <div className="pl-drawer" role="dialog" aria-modal="true" aria-label={tagName(tag)} onClick={(e) => e.stopPropagation()}>
          <p className="apl-leise" style={{ padding: 20 }}>{isPending ? "Termin wird geladen …" : "Den Termin gibt es im Kalender nicht mehr."}</p>
        </div>
      </div>
    );
  }
  return (
    <EditTerminDrawer
      t={t} people={people} kunden={kunden}
      onClose={onClose} onTagesuebersicht={onClose}
      arbeitsplan={{ plan, tag }}
    />
  );
}

function TagOhneDatum({ plan, tag, onClose, onKalender, onOeffnen }: Props & { tag: PlanTag }) {
  const aendern = useArbeitstagAendern(plan.projekt_id);
  const kopie = useArbeitstagKopie(plan.projekt_id);
  const loeschen = useArbeitstagLoeschen(plan.projekt_id);
  const reihe = useReihenfolge(plan.projekt_id);
  const [titel, setTitel] = useState(tag.titel);
  const [notiz, setNotiz] = useState(tag.notiz);
  const [sicher, setSicher] = useState(false);
  useEffect(() => { setTitel(tag.titel); setNotiz(tag.notiz); }, [tag.id, tag.titel, tag.notiz]);

  const ids = plan.tage.map((t) => t.id);
  const stelle = ids.indexOf(tag.id);
  const schieben = (um: number) => {
    const neu = [...ids];
    const ziel = stelle + um;
    if (ziel < 0 || ziel >= neu.length) return;
    [neu[stelle], neu[ziel]] = [neu[ziel], neu[stelle]];
    reihe.mutate(neu);
  };
  const fehler = aendern.error || kopie.error || loeschen.error || reihe.error;

  return (
    <div className="pl-overlay" onClick={onClose}>
      <div className="pl-drawer apl-blatt" role="dialog" aria-modal="true" aria-labelledby="apl-blatt-titel" onClick={(e) => e.stopPropagation()}>
        <header className="apl-blatt-kopf">
          <div style={{ minWidth: 0 }}>
            <button type="button" className="apl-zurueck" onClick={onClose}>‹ Arbeitsplan</button>
            <h2 id="apl-blatt-titel">{tagName(tag)}</h2>
            <p className="apl-ohne-datum">ohne Datum</p>
          </div>
          <button type="button" onClick={onClose} className="pl-closebtn" aria-label="Schließen">×</button>
        </header>

        <div className="apl-blatt-inhalt">
          <div className="apl-karte">
            <label className="apl-feld">
              <span className="apl-cap">Titel</span>
              <input
                value={titel}
                onChange={(e) => setTitel(e.target.value)}
                onBlur={() => { if (titel.trim() && titel !== tag.titel) aendern.mutate({ id: tag.id, patch: { titel } }); }}
              />
            </label>
            <label className="apl-feld">
              <span className="apl-cap">Beschreibung für die Helfer</span>
              <textarea
                rows={2}
                value={notiz}
                placeholder="Steht später am Job in der Team-App"
                onChange={(e) => setNotiz(e.target.value)}
                onBlur={() => { if (notiz !== tag.notiz) aendern.mutate({ id: tag.id, patch: { notiz } }); }}
              />
            </label>
            <ArbeitstagTeil plan={plan} tag={tag} />
            <p className="apl-leise">
              Personen teilst du ein, sobald der Tag ein Datum hat. Führt ein Packzettel aus, steht sein Helfer
              dann mit der Pauschale als ein Knopf bereit.
            </p>
            <div className="apl-blatt-knoepfe">
              <button type="button" className="vg-btn vg-btn-primary" onClick={() => onKalender([tag.id])}>Im Kalender wählen</button>
            </div>
          </div>

          <BedarfListe plan={plan} tag={tag} />

          <div className="apl-blatt-fuss">
            <div className="apl-blatt-knoepfe">
              <button type="button" className="vg-btn" disabled={kopie.isPending}
                onClick={() => kopie.mutate(tag.id, { onSuccess: (d) => onOeffnen(d.id) })}>
                ×2 Tag kopieren
              </button>
              <button type="button" className="vg-btn" disabled={stelle <= 0 || reihe.isPending} onClick={() => schieben(-1)} aria-label="Einen Tag früher">↑ früher</button>
              <button type="button" className="vg-btn" disabled={stelle >= ids.length - 1 || reihe.isPending} onClick={() => schieben(1)} aria-label="Einen Tag später">↓ später</button>
            </div>
            {!sicher ? (
              <button type="button" className="vg-btn apl-gefahr" onClick={() => setSicher(true)}>Tag löschen</button>
            ) : (
              <span className="apl-blatt-knoepfe">
                <span>Tag löschen?</span>
                <button type="button" className="vg-btn apl-gefahr" disabled={loeschen.isPending}
                  onClick={() => loeschen.mutate(tag.id, { onSuccess: onClose })}>Ja, löschen</button>
                <button type="button" className="vg-btn" onClick={() => setSicher(false)}>Nein</button>
              </span>
            )}
          </div>
          {fehler && <p className="apl-fehler" role="alert">{fehlerText(fehler)}</p>}
        </div>
      </div>
    </div>
  );
}
